import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents, LayersControl } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom orange marker for selected location
const orangeIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-orange.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Click handler component (must be child of MapContainer to access map events)
const ClickHandler = ({ onClick }) => {
  useMapEvents({
    click(e) {
      onClick({ latitude: e.latlng.lat, longitude: e.latlng.lng });
    },
  });
  return null;
};

// Component to fit map bounds to markers
const FitBounds = ({ locations }) => {
  const map = useMap();

  useEffect(() => {
    if (locations && locations.length > 0) {
      const bounds = locations.map(loc => [loc.latitude, loc.longitude]);
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 13 });
    }
  }, [locations, map]);

  return null;
};

const MapComponent = ({ 
  locations = [], 
  center = [49.8175, 15.4730], // Czech Republic center
  zoom = 7, 
  height = '400px',
  onLocationSelect,
  selectedLocationId,
  clickable = false,
  useMapyCz = false // Default to OpenStreetMap
}) => {
  return (
    <div style={{ height, width: '100%', borderRadius: '12px', overflow: 'hidden' }}>
      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height: '100%', width: '100%', cursor: clickable ? 'crosshair' : 'grab' }}
        scrollWheelZoom={true}
      >
        {clickable && onLocationSelect && <ClickHandler onClick={onLocationSelect} />}
        <LayersControl position="topright">
          <LayersControl.BaseLayer checked name="🗺️ Mapa">
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="🛰️ Satelit">
            <TileLayer
              attribution='Tiles &copy; Esri'
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              maxZoom={19}
            />
          </LayersControl.BaseLayer>
          {useMapyCz && (
            <LayersControl.BaseLayer name="🌲 Outdoor (Mapy.cz)">
              <TileLayer
                attribution='&copy; <a href="https://mapy.cz/">Mapy.cz</a>'
                url="https://api.mapy.cz/v1/maptiles/outdoor/256/{z}/{x}/{y}?apikey=OTkxODQ4MjItMTQ5Mi00NWE3LTk0ZDUtMDJkMWQzNmE1YTkx"
                minZoom={2}
                maxZoom={19}
              />
            </LayersControl.BaseLayer>
          )}
        </LayersControl>
        
        {locations && locations.length > 0 && (
          <>
            {locations.map((location) => (
              <Marker
                key={location.id}
                position={[location.latitude, location.longitude]}
                icon={selectedLocationId === location.id || clickable ? orangeIcon : new L.Icon.Default()}
              >
                <Popup>
                  <div className="p-1" style={{ minWidth: 200 }}>
                    {(location.photos && location.photos[0]) || location.image ? (
                      <img
                        src={location.photos?.[0] || location.image}
                        alt={location.name}
                        style={{
                          width: '100%',
                          height: 110,
                          objectFit: 'cover',
                          borderRadius: 8,
                          marginBottom: 8,
                        }}
                      />
                    ) : null}
                    <h3 className="font-bold text-base mb-1">{location.name}</h3>
                    {location.description && (
                      <p className="text-xs text-gray-700 mb-1 line-clamp-3" data-testid="map-popup-description">
                        {location.description}
                      </p>
                    )}
                    {location.type && (
                      <p className="text-xs text-gray-500 mb-2">{location.type}</p>
                    )}
                    <p className="font-semibold text-orange-600">{location.price} Kč / noc</p>
                    {!clickable && (
                      <button
                        onClick={() => window.location.href = `/location/${location.id}`}
                        className="mt-2 text-sm text-blue-600 hover:underline"
                      >
                        Zobrazit detail →
                      </button>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}
            {!clickable && <FitBounds locations={locations} />}
          </>
        )}
      </MapContainer>
    </div>
  );
};

export default MapComponent;
